import * as React from "react"
import { Box, Checkbox, FormControlLabel, Tooltip, } from "@mui/material"
import { AXIOS_API_INSTANCE } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop";
import { useSnackbar } from "notistack";
import Chart from 'react-apexcharts'
import { findUser, useUsers } from "../ReactQuery/QueryUsers";


export default function ServiceOrderServiceWorkload({ selectedLocations, groupByAssignee = false, callbackLoading, showLocationName = true, breakout_locations = true, height = 450 }) {
    const { enqueueSnackbar } = useSnackbar();
    const { data: users, isLoadingUsers } = useUsers();
    const [isLoading, setIsLoading] = React.useState(false)
    const [workTimes, setWorkTimes] = React.useState({})

    const getReport = (location_ids, groupByAssignee) => {
        if (!location_ids || location_ids.length === 0) {
            return
        }
        setIsLoading(true)
        callbackLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("reports/serviceorder_service_work_load", {
                params: {
                    location_ids: location_ids,
                    group_by_assignee: groupByAssignee
                },
            }).then((report) => {
                setWorkTimes(report.data.data)
                setIsLoading(false)
                callbackLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                callbackLoading(false)
                enqueueSnackbar(`Failed to load data <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    React.useEffect(() => {
        setWorkTimes({})
        getReport(selectedLocations.map((x) => x.id), groupByAssignee)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocations, groupByAssignee])

    let series = []
    const found_users = {}
    if (groupByAssignee) {
        Object.keys(workTimes).forEach((l) => {
            Object.keys(workTimes[l]).forEach((u) => {
                found_users[u] = found_users[u] ?? {}
                found_users[u][l] = true
            })
        })
        series.push.apply(series, [
            {
                name: `Pending - No Approval Requested`,
                data: []
            },
            {
                name: `Pending - Approval Requested`,
                data: []
            },
            {
                name: `Pending - Customer Approved`,
                data: []
            },
            {
                name: `In Progress`,
                data: []
            }
        ])
        if (breakout_locations) {
            selectedLocations.forEach((l) => {
                Object.keys(found_users).forEach((u) => {
                    if (!found_users[u][l.id]) {
                        return
                    }
                    const user = findUser(users, u) ?? { name: 'Unassigned', id: 'null' };
                    series[series.length - 4].data.push((workTimes[l.id]?.[user.id]?.PENDING?.NOT_REQUESTED ?? 0) / 60 || null)
                    series[series.length - 3].data.push((workTimes[l.id]?.[user.id]?.PENDING?.APPROVAL_REQUESTED ?? 0) / 60 || null)
                    series[series.length - 2].data.push((workTimes[l.id]?.[user.id]?.PENDING?.APPROVED ?? 0) / 60 || null)
                    series[series.length - 1].data.push(((workTimes[l.id]?.[user.id]?.IN_PROGRESS?.NOT_REQUESTED ?? 0) + (workTimes[l.id]?.[user.id]?.IN_PROGRESS?.APPROVAL_REQUESTED ?? 0) + (workTimes[l.id]?.[user.id]?.IN_PROGRESS?.APPROVED ?? 0)) / 60 || null)
                })
            })
        } else {
            Object.keys(found_users).forEach((u) => {
                const user = findUser(users, u) ?? { name: 'Unassigned', id: 'null' };
                let not_requested = 0;
                let approval_requested = 0;
                let approved = 0;
                let in_progress = 0;
                Object.keys(workTimes).forEach((l) => {
                    not_requested += workTimes[l]?.[user.id]?.PENDING?.NOT_REQUESTED ?? 0
                    approval_requested += workTimes[l]?.[user.id]?.PENDING?.APPROVAL_REQUESTED ?? 0
                    approved += workTimes[l]?.[user.id]?.PENDING?.APPROVED ?? 0
                    in_progress += (workTimes[l]?.[user.id]?.IN_PROGRESS?.NOT_REQUESTED ?? 0) + (workTimes[l]?.[user.id]?.IN_PROGRESS?.APPROVAL_REQUESTED ?? 0) + (workTimes[l]?.[user.id]?.IN_PROGRESS?.APPROVED ?? 0)
                })
                series[series.length - 4].data.push(not_requested / 60 || null)
                series[series.length - 3].data.push(approval_requested / 60 || null)
                series[series.length - 2].data.push(approved / 60 || null)
                series[series.length - 1].data.push(in_progress / 60 || null)
            })
        }
    } else {
        series = [
            {
                name: 'Pending - No Approval Requested',
                data: selectedLocations.map((l) => [(workTimes?.[l.id]?.PENDING?.NOT_REQUESTED ?? 0) / 60 || null, null]),
            },
            {
                name: 'Pending - Approval Requested',
                data: selectedLocations.map((l) => [(workTimes?.[l.id]?.PENDING?.APPROVAL_REQUESTED ?? 0) / 60 || null, null]),
            },
            {
                name: 'Pending - Customer Approved',
                data: selectedLocations.map((l) => [(workTimes?.[l.id]?.PENDING?.APPROVED ?? 0) / 60 || null, null]),
            },
            {
                name: 'In Progress',
                data: selectedLocations.map((l) => [null, ((workTimes?.[l.id]?.IN_PROGRESS?.APPROVED ?? 0) + (workTimes?.[l.id]?.IN_PROGRESS?.NOT_REQUESTED ?? 0) + (workTimes?.[l.id]?.IN_PROGRESS?.APPROVAL_REQUESTED ?? 0)) / 60 || null]),
            },
        ]
        if (breakout_locations) {
            series.forEach((s) => {
                s.data = s.data.flatMap(x => x)
            })
        } else {
            series.forEach((s) => {
                s.data = s.data.reduce((acc, val) => {
                    if (val[0] !== null) {
                        acc[0] += val[0]
                    }
                    if (val[1] !== null) {
                        acc[1] += val[1]
                    }
                    return acc
                }, [null, null])
            })
        }
    }

    return (
        <React.Fragment>
            <Box>
                {!isLoading && Object.keys(workTimes).length === 0 &&
                    <h3>NO DATA</h3>
                }
                {!isLoading && !isLoadingUsers && Object.keys(workTimes).length > 0 &&
                    <Chart
                        type="bar" height={height}
                        options={{
                            chart: {
                                height: height,
                                type: 'bar',
                                stacked: true,
                                width: '100%',
                                toolbar: {
                                    show: true
                                },
                            },
                            plotOptions: {
                                bar: {
                                    horizontal: false,
                                    columnWidth: '55%',
                                    borderRadius: 5,
                                    borderRadiusApplication: 'end',
                                },
                            },
                            colors: ['#04847e', '#02784c', '#016f26', '#EE8434', '#a77b24', '#77751a'],
                            dataLabels: {
                                total: {
                                    enabled: true,
                                    style: {
                                        fontSize: '13px',
                                        fontWeight: 900
                                    },
                                },
                                formatter: function (val) {
                                    return Math.round(val * 10) / 10
                                },
                            },
                            stroke: {
                                show: true,
                                width: 2,
                                colors: ['transparent']
                            },
                            xaxis: {
                                type: 'category',
                                categories: Array.from(breakout_locations ? selectedLocations : { length: 1 }, (l) => {
                                    console.log(l)
                                    if (groupByAssignee) {
                                        if (breakout_locations) {
                                            return Object.keys(found_users).filter((u) => found_users[u][l.id]).map((u) => findUser(users, u)?.name ?? 'Unassigned')
                                        } else {
                                            return Object.keys(found_users).map((u) => findUser(users, u)?.name ?? 'Unassigned')
                                        }
                                    } else {
                                        return ['Pending', 'In Progress']
                                    }
                                }).flat(),
                                labels: {
                                    rotate: -45,
                                    tickPlacement: 'on'
                                },
                                group: {
                                    style: {
                                        fontSize: '10px',
                                        fontWeight: 700
                                    },
                                    groups: showLocationName ? selectedLocations.map((l) => { return { title: l.name, cols: groupByAssignee ? Object.keys(found_users).filter((u) => !breakout_locations || found_users[u][l.id]).length : 2 } }) : []
                                }
                            },
                            yaxis: {
                                decimalsInFloat: 1,
                                title: {
                                    text: 'Total Hours'
                                }
                            },
                            fill: {
                                opacity: 1
                            },
                            tooltip: {
                                y: {
                                    formatter: function (val) {
                                        return `${Math.round(val * 10) / 10} hours`
                                    }
                                }
                            }
                        }}
                        series={series}
                    />
                }
            </Box>
        </React.Fragment>
    )
}


export function ServiceOrderServiceWorkloadWrapper({ selectedLocations }) {
    const [groupByAssignee, setGroupByAssignee] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(false)

    return (
        <Box>
            <LoadingBackdropIndeterminate
                open={isLoading}
                text="Loading Work Load Report"
            />
            <p>Total time estimated for incomplete services</p>
            <Tooltip
                title="View estimated time for incomplete services per technician"
                arrow
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={groupByAssignee}
                            onChange={(event) => { setGroupByAssignee(event.target.checked) }}
                        />
                    }
                    label="Group by Assigned Technician"
                />
            </Tooltip>
            <ServiceOrderServiceWorkload selectedLocations={selectedLocations} groupByAssignee={groupByAssignee} callbackLoading={setIsLoading} />
        </Box>
    )
}